/**
 * Copyright (c) 2025, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./Login.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { AuthConsumer } from "../../../AuthContext";
import { simpleFetch } from "../../../restWrapper";
import { PLATFORM } from "../../../config";
import plugLogo from "../../../assets/component/images/plug-impact-withText.png";
import plugMark from "../../../assets/component/images/plug-logo.svg";

// Registration is shaped for nonprofits and asks for a contact person and a
// card statement descriptor. A business card collects five things, so the rest
// is derived from the business name. What is derived is only what is safe to
// derive: a subdomain and a statement descriptor, both editable in settings and
// neither a claim about the business. The phone number is asked for, because it
// reaches receipts and Stripe and a made-up one is a wrong fact rather than a
// placeholder. The nonprofit Plug flow (CP-304) collects contact fields in a
// dedicated second step; director, secretary and the donation-page subdomain
// are filled in during CheckPlus onboarding.

// Native names, in the order the marketing nav (PlugLanding/data.js) and the
// workspace header list them, so the control does not reshuffle between the
// three places a visitor meets it.
const LANGUAGE_LABELS = { zh: "繁體中文", cn: "简体中文", en: "English" };

/**
 * The legal pages live in the org-admin route tree, one set per language
 * (/EnTerms, /ZhPrivacyPolicy and so on), and the rest of CheckPlus links to
 * the PrivacyPolicy variant rather than the shorter /EnPrivacy page -- see
 * EnSecurity, EnStripe, EnContact. Matching that here keeps one privacy page
 * for the whole product.
 *
 * Opened in a new tab rather than navigated to: this sits under a part-filled
 * sign-up form, and sending someone to a policy page would throw away what
 * they had typed.
 */
const LEGAL_PREFIX = { zh: "Zh", cn: "Cn", en: "En" };
const legalHref = (language, page) =>
  "/" + (LEGAL_PREFIX[language] || "En") + page;

// firstName and lastName are required and there is no person to ask about, so
// the organisation's name stands in for the account holder until settings.
const splitName = (fullName) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: "Plug", lastName: "User" };
  }
  if (parts.length === 1) {
    return { firstName: parts[0].slice(0, 20), lastName: "Account" };
  }
  return {
    firstName: parts[0].slice(0, 20),
    lastName: parts.slice(1).join(" ").slice(0, 20) || "Account",
  };
};

// The validator wants alphanumerics only, and a subdomain has to be unique
// across every organisation. Two businesses of the same name would otherwise
// ask for the same one, so the name only starts it: a timestamp separates
// registrations, and the random tail separates two in the same millisecond.
// There is no length limit on the field, only isAlphanumeric.
const slugSubdomain = (name) => {
  const base = (name || "plug")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 18);
  const tail = Math.random().toString(36).replace(/[^a-z0-9]/g, "").slice(0, 4);
  return (base || "plug") + Date.now().toString(36) + tail;
};

// 15 characters is Stripe's limit for what appears on a card statement, and
// the validator enforces the same.
const statementFromName = (name) => {
  const cleaned = (name || "Plug").replace(/[^a-zA-Z0-9 ]/g, "").trim();
  return (cleaned || "Plug").slice(0, 15);
};

// One door, three account types — the type is chosen once, at sign-up.
// Sign in is wired to real auth. Individual, Business and Nonprofit are all
// wired; Nonprofit uses a dedicated two-step Plug flow (CP-304) rather than
// inventing the governance and contact fields the way Business still does.
class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // "Create a free account" on the public impact page opens this on sign-up.
      authMode: props.vm.loginMode === "signup" ? "signup" : "signin",
      signupRole: "individual",
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      businessName: "",
      brNo: "",
      // Nonprofit card / details (CP-304). Section 88 is deliberately not
      // collected here — that and Stripe payout setup land later on CheckPlus.
      organizationName: "",
      // The six digits emailed by /donors/register. An individual account is
      // unusable until they are entered — POST /login answers 403 for an
      // unverified account — so registration hands straight over to the pane
      // that collects them (CP-303).
      verifyCode: "",
      phone: "",
      submitting: false,
      errMsg: "",
      // Whether errMsg is "this address already has an account", which is the
      // one error with somewhere to go: it earns a way through to sign-in.
      errTaken: false,
      // What the address check last found, so submitting cannot ask the server
      // something the field has already been told. { value, status } where
      // status is idle | checking | free | org | other; "idle" also covers a
      // check that could not be made, and says nothing.
      emailCheck: { value: "", status: "idle" },
      successMsg: "",
      // The address a reset link was just asked for. Held apart from the
      // email field because the confirmation names the address that was
      // actually submitted, and the field behind it stays editable.
      forgotSentTo: "",
      // The first-run screens (CP-294, CP-295). consentKind is the kind of
      // account waiting on one; consentAccount is who it names, and stays null
      // when the profile could not be read — the pane then shows a retry
      // rather than a half-filled account row.
      consentKind: null,
      // The welcome's two agreements. Both are required before the workspace
      // opens, so they start off and the CTA stays inert until they are on.
      consentAgreeAccount: false,
      consentAgreePayments: false,
      // Whether the account waiting on the pane also owes its profile
      // questions (CP-307). Carried from the load that put it here, because
      // the pane is no longer what decides that -- see afterAuth.
      consentOnboarding: false,
      // And whether it owes the guided tour (CP-308) — the offer after the
      // questions, carried from the same load for the same reason.
      consentTour: false,
      consentAccount: null,
      consentBusy: false,
      consentErr: "",
    };
  }

  /**
   * Apply the result of a request only if the pane that asked for it is
   * still the pane on screen.
   *
   * submitting is shared by every pane, and the sign-in and reset panes are
   * one click apart in both directions. Without this an answer lands
   * wherever the person happens to be standing: a failed sign-in writes
   * "wrong email or password" into the reset pane, and a reset that
   * finishes after "Back to sign in" drags someone off to a confirmation
   * they walked away from. Leaving a pane already clears submitting, so a
   * result dropped here strands nothing behind it.
   */
  settle = (mode, next) =>
    this.setState((s) => (s.authMode === mode ? next : null));

  setSignin = () =>
    this.setState({ authMode: "signin", submitting: false, errMsg: "", errTaken: false, successMsg: "" });

  setSignup = () =>
    this.setState({ authMode: "signup", submitting: false, errMsg: "", errTaken: false, successMsg: "" });

  // The password is dropped on the way in and never asked for again --
  // the whole premise of the pane is that it is not known. The email is
  // kept, since it is almost always the one the reset should go to.
  setForgot = () =>
    this.setState({
      authMode: "forgot",
      submitting: false,
      password: "",
      errMsg: "",
      errTaken: false,
      successMsg: "",
    });

  pickRole = (id) => () => this.setState({ signupRole: id, errMsg: "", errTaken: false });

  onField = (name) => (e) =>
    this.setState({ [name]: e.target.value, errMsg: "", errTaken: false });

  // PhoneInput reports the value itself rather than an event, and reports
  // undefined when the field is emptied.
  onPhone = (value) => this.setState({ phone: value || "", errMsg: "", errTaken: false });

  // Sign in with the address already typed. The password is dropped rather
  // than carried across: it was being chosen for a new account, and it is not
  // the one that belongs to the account that already exists.
  goSignInWithEmail = () =>
    this.setState({
      authMode: "signin",
      submitting: false,
      password: "",
      errMsg: "",
      errTaken: false,
      successMsg: "",
    });

  // What to say about an address that is already in use. An organisation is
  // told to sign in — its account is the account it wants, on both products.
  // An individual account is a different thing wearing the same address, and
  // cannot become an organisation, so it is told as much.
  takenMessage = (status) => {
    const m = this.props.vm.m;
    return status === "other" ? m.loginErrEmailIndividual : m.loginErrUserExists;
  };

  /**
   * Is this address already an account?
   *
   * Asked when the email field is left, not only on submit: an organisation
   * that already has a CheckPlus account cannot make a second one here — it
   * signs in — and being told that after filling in five fields is worse than
   * being told at the field itself.
   *
   * expectedType is sent and `platform` deliberately is not. The platform
   * check on that endpoint answers "is this account the type this product
   * signs in", and by its reckoning a nonprofit on Plug is a mismatch; by ours
   * it is exactly who this card is for.
   */
  checkEmail = () => {
    const username = this.state.email.trim();
    // Nothing to ask about something that cannot be an address yet.
    if (!username || username.indexOf("@") < 1) {
      return;
    }
    const last = this.state.emailCheck;
    if (last.value === username && last.status !== "checking") {
      return;
    }
    this.setState({ emailCheck: { value: username, status: "checking" } });
    simpleFetch("/organizations/username", "POST", {
      username: username,
      expectedType: "organization",
    })
      .then((res) => {
        const body = (res && res.body) || {};
        let status = "idle";
        if (res && res.status === 404) {
          status = "free";
        } else if (res && res.status === 200) {
          // 200 carries the verdict: an organisation, or an account that is
          // not one. 204 is the endpoint's older bodyless "it exists".
          status = body.organization === false ? "other" : "org";
        } else if (res && res.status === 204) {
          status = "org";
        }
        const taken = status === "org" || status === "other";
        // Read against the state it is written to, and say nothing at all
        // about an address that is no longer the one in the field.
        this.setState((s) =>
          s.email.trim() !== username
            ? null
            : {
              emailCheck: { value: username, status: status },
              errMsg: taken ? this.takenMessage(status) : s.errMsg,
              errTaken: taken,
            }
        );
      })
      // A check that could not be made says nothing at all; register still
      // refuses a duplicate, and a message about a working address would be
      // worse than no message.
      .catch(() =>
        this.setState({ emailCheck: { value: username, status: "idle" } })
      );
  };

  // One form for both account types; marketplaceLogin reports which kind of
  // account signed in and the marketplace routes accordingly.
  submit = (marketplaceLogin) => () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    if (!this.state.email.trim() || !this.state.password) {
      this.setState({ errMsg: m.loginErrRequired });
      return;
    }
    const mode = this.state.authMode;
    this.setState({ submitting: true, errMsg: "" });
    marketplaceLogin({ username: this.state.email.trim(), password: this.state.password })
      .then((res) => {
        if (res.kind) {
          return this.afterAuth(res.kind);
        }
        // 403 is the one status here that is not about the password:
        // postLogin throws NOT_VERIFIED for an account that never entered its
        // emailed code, and telling that person their password is wrong sends
        // them to reset a password that was always right.
        const byStatus = { 403: m.loginErrNotVerified, 422: m.loginErrCredentials, 429: m.loginErrTooMany };
        this.settle(mode, { submitting: false, errMsg: byStatus[res.status] || m.loginErrGeneric });
      })
      .catch(() => this.settle(mode, { submitting: false, errMsg: m.loginErrGeneric }));
  };

  /**
   * Ask for a password reset link.
   *
   * One request covers every account type. POST /donors/forgot and POST
   * /organizations/forgot are the same handler over the same collection
   * (routes/common/users.js postForgot), and it looks an account up by
   * username alone before picking the donor or the organisation mail off
   * whatever it found. So there is nothing to decide here, and nothing to
   * retry against the other endpoint.
   *
   * The brand of that mail is decided by both ends. postForgot reaches Plug
   * on either signal -- the account's own sourcePlatform, or the front end
   * the reset was asked from -- so someone who signed up on CheckPlus and
   * then hit this form on Plug gets the Plug layout and a link on the Plug
   * domain. This end owns the second half of that: the server reads an
   * Origin header when it can, but both brands are served from the same
   * host in local and preview builds, so the header cannot tell them apart
   * and PLATFORM is what actually carries it. Omit it and a Plug reset
   * arrives wearing CheckPlus (CP-306).
   *
   * The language is the other part this end owns -- left out, the mail
   * falls back to Config.definedLanguage whatever the person is reading.
   *
   * A missing account answers 404, and saying so would turn this form into
   * an address checker for anyone who wanted one, so sent and unknown land
   * on the same confirmation. Every other answer is about this request
   * rather than about who holds the address, and a confirmation would leave
   * someone waiting on mail that is never coming: too many attempts, a
   * server that failed, and 422, which is the validator refusing the
   * address as an address (validators/users.js runs isEmail) rather than an
   * answer about whether it has an account. The check on the way in here is
   * only "@ somewhere past the first character", so it passes things
   * isEmail refuses -- person@gmail has no TLD -- and those must not be
   * told mail is on its way.
   */
  sendReset = () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    const username = this.state.email.trim();
    if (!username || username.indexOf("@") < 1) {
      this.setState({ errMsg: m.loginForgotErrEmail });
      return;
    }
    const mode = this.state.authMode;
    this.setState({ submitting: true, errMsg: "" });
    simpleFetch("/donors/forgot", "POST", {
      username: username,
      sourcePlatform: PLATFORM,
      language: this.props.vm.language || "en",
    })
      .then((res) => {
        // simpleFetch resolves rather than rejects when the request never
        // reached the server, handing back the Error itself, so catch() is
        // not enough to notice that -- anything without a numeric status is
        // a failure to send, not an answer about the address.
        const status = res && typeof res.status === "number" ? res.status : 0;
        if (status === 0 || status >= 500) {
          this.settle(mode, { submitting: false, errMsg: m.loginErrGeneric });
          return;
        }
        if (status === 429) {
          this.settle(mode, { submitting: false, errMsg: m.loginErrTooMany });
          return;
        }
        if (status === 422) {
          this.settle(mode, { submitting: false, errMsg: m.loginForgotErrEmail });
          return;
        }
        this.settle(mode, {
          submitting: false,
          errMsg: "",
          authMode: "forgotSent",
          forgotSentTo: username,
        });
      })
      .catch(() => this.settle(mode, { submitting: false, errMsg: m.loginErrGeneric }));
  };

  /**
   * Create a business account.
   *
   * Nonprofits do not come through here: their card opens the dedicated Plug
   * details step (CP-304), which asks for a name, an email and a password and
   * leaves Section 88 and payouts to CheckPlus onboarding.
   *
   * Registration does not sign anyone in: the account is created unverified
   * and a confirmation email is sent, so on success this returns to the
   * sign-in tab with the address already filled and says to go and check.
   */
  registerBusiness = () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    const organizationName = this.state.businessName.trim();
    const registrationNo = this.state.brNo.trim();
    const username = this.state.email.trim();
    const password = this.state.password;
    const phone = this.state.phone.trim();

    if (!organizationName || !registrationNo || !username || !password || !phone) {
      this.setState({ errMsg: m.loginErrSignupRequired });
      return;
    }
    if (password.length < 8) {
      this.setState({ errMsg: m.loginErrPasswordLength });
      return;
    }
    // An address the field check has already found says so again rather than
    // asking the server a question it has been answered.
    const checked = this.state.emailCheck;
    if (
      checked.value === username &&
      (checked.status === "org" || checked.status === "other")
    ) {
      this.setState({
        errMsg: this.takenMessage(checked.status),
        errTaken: true,
      });
      return;
    }
    // Settings validates the phone number the same way, so anything accepted
    // here can be edited there afterwards. A number that only passed the
    // backend's "at least one character" would fail the moment the business
    // opened settings to change something else.
    //
    // The leading + is checked separately because isValidPhoneNumber runs
    // against this package's minimal metadata, which answers a question
    // about length rather than about the number: a bare local number
    // satisfies it and then reaches Stripe and the receipt with no country
    // on it. The field asks for an international number, so anything typed
    // into it carries the code already.
    if (phone.charAt(0) !== "+" || !isValidPhoneNumber(phone)) {
      this.setState({ errMsg: m.loginErrPhoneInvalid });
      return;
    }

    const { firstName, lastName } = splitName(organizationName);
    this.setState({ submitting: true, errMsg: "", errTaken: false, successMsg: "" });

    return simpleFetch("/organizations/register", "POST", {
      username,
      password,
      firstName,
      lastName,
      phoneNumber: phone,
      organizationName,
      organizationIRDNo: registrationNo,
      subDomain: slugSubdomain(organizationName),
      statementDescriptor: statementFromName(organizationName),
      directorName: organizationName.slice(0, 80) || "Director",
      secretaryName: organizationName.slice(0, 80) || "Secretary",
      buttonColor: "FFFFFF",
      headerColor: "2B91D6",
      progressBarColor: "2B91D6",
      organizationType: "business",
    })
      .then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
          this.setState({
            submitting: false,
            authMode: "signin",
            email: username,
            password: "",
            businessName: "",
            brNo: "",
            phone: "",
            successMsg: m.loginCreatedCheckEmail,
            errMsg: "",
            errTaken: false,
          });
          return true;
        }
        // The server's own message is more useful than a generic one, except
        // where a case reads better in the user's own language. Matched on
        // which message it is rather than on the word "already", which three of
        // them share: a Section 88 number another organisation already has was
        // otherwise reported as a taken email address, which is a different
        // field and a different fix.
        const message = (res && res.body && res.body.message) || "";
        const text = message.toLowerCase();
        const isTaken = text.indexOf("user already exist") !== -1;
        let errMsg = message || m.loginErrSignupGeneric;
        if (isTaken) {
          errMsg = m.loginErrUserExists;
        } else if (text.indexOf("organization number already exist") !== -1) {
          errMsg = m.loginErrOrgNoExists;
        } else if (text.indexOf("subdomain is already taken") !== -1) {
          // The subdomain is derived, so there is no field to correct — a
          // second press derives a different one.
          errMsg = m.loginErrSubdomainTaken;
        }
        this.setState({ submitting: false, errMsg: errMsg, errTaken: isTaken });
        return false;
      })
      .catch(() => {
        this.setState({ submitting: false, errMsg: m.loginErrSignupGeneric });
        return false;
      });
  };

  /**
   * Create an individual account — a CheckPlus donor, the same account that
   * `/donors/login` already authenticates on Plug (CP-303).
   *
   * Unlike the business card this invents nothing. Every field the validator
   * requires is asked for, including the phone number: it accepts any
   * non-empty string, which is exactly why defaulting one would go unnoticed.
   */
  registerIndividual = () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    const firstName = this.state.firstName.trim();
    const lastName = this.state.lastName.trim();
    const username = this.state.email.trim();
    const phoneNumber = this.state.phoneNumber.trim();
    const password = this.state.password;

    if (!firstName || !lastName || !username || !phoneNumber || !password) {
      this.setState({ errMsg: m.loginErrSignupRequired });
      return;
    }
    if (password.length < 8) {
      this.setState({ errMsg: m.loginErrPasswordLength });
      return;
    }

    this.setState({ submitting: true, errMsg: "", successMsg: "" });

    // isCustomDomain is passed unconditionally rather than read from the URL.
    // It is what sets x-checkplus-external, and the JWT comes back only for an
    // external request — on Plug's main domain the flag would otherwise be
    // false and registration would return no token at all.
    return simpleFetch(
      "/donors/register",
      "POST",
      {
        username,
        password,
        firstName,
        lastName,
        phoneNumber,
        language: this.props.vm.language,
        // Which front end this account was created on. Nothing downstream can
        // work it out later — a marketplace sign-up has no referring
        // organisation to read a type off — so it is recorded here and every
        // branded email after this one reads it back (CP-306).
        sourcePlatform: PLATFORM,
      },
      false,
      { isCustomDomain: true }
    )
      .then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
          if (res.status === 200 && res.body && res.body.accessToken) {
            localStorage.setItem("accessToken", res.body.accessToken);
            localStorage.setItem("expiredIn", res.body.expiredIn);
          }
          // Registration signs them in already, but an unverified account
          // cannot sign in a second time, so the code is collected now rather
          // than left to a later visit that would be refused.
          this.setState({
            submitting: false,
            authMode: "verify",
            verifyCode: "",
            errMsg: "",
          });
          return true;
        }
        const message = (res && res.body && res.body.message) || "";
        const already = message.toLowerCase().indexOf("already") !== -1;
        this.setState({
          submitting: false,
          errMsg: already
            ? m.loginErrUserExists
            : message || m.loginErrSignupGeneric,
        });
        return false;
      })
      .catch(() => {
        this.setState({ submitting: false, errMsg: m.loginErrSignupGeneric });
        return false;
      });
  };

  /**
   * Confirm the emailed code, then open the workspace.
   *
   * Consent is recorded here rather than left to the first-run pane: CP-294
   * exists to authorize handing an *existing* CheckPlus account to a separate
   * company, and this account was created on Plug a minute ago. Asking it to
   * authorize itself would be the first thing a new user ever saw.
   *
   * Signing in again afterwards is deliberate. It is what puts the session
   * into AuthContext, which everything downstream of enterSession assumes has
   * already happened.
   */
  verify = (marketplaceLogin) => () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    const code = this.state.verifyCode.trim();
    if (!/^\d{6}$/.test(code)) {
      this.setState({ errMsg: m.loginErrVerifyInvalid });
      return;
    }
    this.setState({ submitting: true, errMsg: "" });

    return simpleFetch("/donors/verify-code", "POST", { code }, false, {
      isCustomDomain: true,
    })
      .then((res) => {
        if (!res || ![200, 204].includes(res.status)) {
          const message = (res && res.body && res.body.message) || "";
          this.setState({
            submitting: false,
            errMsg: message || m.loginErrVerifyGeneric,
          });
          return;
        }
        if (res.status === 200 && res.body && res.body.accessToken) {
          localStorage.setItem("accessToken", res.body.accessToken);
          localStorage.setItem("expiredIn", res.body.expiredIn);
        }
        // A failure to record consent is not worth blocking on: the account is
        // verified either way, and the only cost is that afterAuth shows the
        // first-run pane once. Better a redundant screen than a new user
        // stranded one step from their dashboard.
        return this.props.vm
          .recordPlugConsent()
          .then(() =>
            marketplaceLogin({
              username: this.state.email.trim(),
              password: this.state.password,
            })
          )
          .then((auth) => {
            if (auth && auth.kind) {
              return this.afterAuth(auth.kind);
            }
            // Verification succeeded, so the account is usable and the pane
            // has nothing left to collect — staying on it would be a dead end
            // with the tabs hidden. Fall back to sign-in, which now works.
            this.setState({
              submitting: false,
              authMode: "signin",
              password: "",
              verifyCode: "",
              errMsg: "",
              successMsg: m.loginVerifiedSignIn,
            });
          });
      })
      .catch(() =>
        this.setState({ submitting: false, errMsg: m.loginErrVerifyGeneric })
      );
  };

  // The nonprofit card opens a dedicated Plug details step (CP-304) rather
  // than inventing governance fields the way the business card still does.
  submitSignup = () => {
    if (this.state.signupRole === "business") {
      this.registerBusiness();
    } else if (this.state.signupRole === "individual") {
      this.registerIndividual();
    } else if (this.state.signupRole === "nonprofit") {
      this.beginNonprofitDetails();
    }
  };

  /**
   * First nonprofit card step — organisation name, email and password only.
   * Section 88 and Stripe payout details are completed later on CheckPlus;
   * this card is the claim-your-page door, not "start receiving".
   */
  beginNonprofitDetails = () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    const organizationName = this.state.organizationName.trim();
    const username = this.state.email.trim();
    const password = this.state.password;
    if (!organizationName || !username || !password) {
      this.setState({ errMsg: m.loginErrSignupRequired });
      return;
    }
    if (password.length < 8) {
      this.setState({ errMsg: m.loginErrPasswordLength });
      return;
    }
    // An address the field check has already found says so here rather than
    // sending someone through the details step to be refused at the end of
    // it -- the same guard registerBusiness applies before it posts.
    const checked = this.state.emailCheck;
    if (
      checked.value === username &&
      (checked.status === "org" || checked.status === "other")
    ) {
      this.setState({
        errMsg: this.takenMessage(checked.status),
        errTaken: true,
      });
      return;
    }
    this.setState({
      authMode: "nonprofitDetails",
      errMsg: "",
      successMsg: "",
    });
  };

  /**
   * Create a CheckPlus nonprofit from Plug (CP-304).
   *
   * Every required registration field is typed — nothing is invented. Section
   * 88 / IRD and Stripe bank/KYC are deferred to the CheckPlus setup flow, so
   * the account can claim a page and publish impact without being listed or
   * payable on the marketplace yet.
   */
  registerNonprofit = () => {
    const m = this.props.vm.m;
    if (this.state.submitting) {
      return;
    }
    const organizationName = this.state.organizationName.trim();
    const username = this.state.email.trim();
    const password = this.state.password;
    const firstName = this.state.firstName.trim();
    const lastName = this.state.lastName.trim();
    const phoneNumber = this.state.phoneNumber.trim();

    if (
      !organizationName ||
      !username ||
      !password ||
      !firstName ||
      !lastName ||
      !phoneNumber
    ) {
      this.setState({ errMsg: m.loginErrSignupRequired });
      return;
    }
    if (password.length < 8) {
      this.setState({ errMsg: m.loginErrPasswordLength });
      return;
    }

    this.setState({ submitting: true, errMsg: "", successMsg: "" });

    return simpleFetch("/organizations/register", "POST", {
      username,
      password,
      firstName,
      lastName,
      phoneNumber,
      organizationName,
      // Placeholder until the org sets its donation page on CheckPlus — same
      // uniqueness strategy as the business card (slug + timestamp).
      subDomain: slugSubdomain(organizationName),
      statementDescriptor: statementFromName(organizationName),
      buttonColor: "FFFFFF",
      headerColor: "2B91D6",
      progressBarColor: "2B91D6",
      organizationType: "nonprofit",
      // Which front end this organisation was created on. The backend stores
      // it so deferred payout setup and Plug-origin emails can resolve later
      // without inventing a referer sniff (CP-304).
      sourcePlatform: PLATFORM,
      language: this.props.vm.language,
    })
      .then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
          this.setState({
            submitting: false,
            authMode: "signin",
            email: username,
            password: "",
            organizationName: "",
            firstName: "",
            lastName: "",
            phoneNumber: "",
            successMsg: m.loginCreatedNonprofitCheckEmail,
            errMsg: "",
          });
          return true;
        }
        // Matched on which message it is rather than on the word "already",
        // which three of them share: a registration number another
        // organisation already has was otherwise reported as a taken email
        // address, which is a different field and a different fix. Same
        // mapping as registerBusiness, for the same reason.
        const message = (res && res.body && res.body.message) || "";
        const text = message.toLowerCase();
        const isTaken = text.indexOf("user already exist") !== -1;
        let errMsg = message || m.loginErrSignupGeneric;
        if (isTaken) {
          errMsg = m.loginErrUserExists;
        } else if (text.indexOf("organization number already exist") !== -1) {
          errMsg = m.loginErrOrgNoExists;
        } else if (text.indexOf("subdomain is already taken") !== -1) {
          // The subdomain is derived, so there is no field to correct — a
          // second press derives a different one.
          errMsg = m.loginErrSubdomainTaken;
        }
        // errTaken is what puts "Sign in instead" on screen, which is the
        // only way out of an address that already has an account.
        this.setState({ submitting: false, errMsg: errMsg, errTaken: isTaken });
        return false;
      })
      .catch(() => {
        this.setState({ submitting: false, errMsg: m.loginErrSignupGeneric });
        return false;
      });
  };

  backFromNonprofitDetails = () => {
    if (this.state.submitting) {
      return;
    }
    this.setState({
      authMode: "signup",
      signupRole: "nonprofit",
      errMsg: "",
    });
  };

  /**
   * The password was accepted. Load the account, then either open the
   * workspace or hold here for the first-run screen (CP-294, CP-295).
   *
   * This is the seam the two screens hang off: it is deliberately between
   * authentication and routing, so nothing about the workspace has been
   * revealed yet when the pane appears.
   */
  afterAuth = (kind) =>
    this.props.vm.loadSession(kind).then((session) => {
      // A business has no first-run *consent* screen to owe. CP-295's welcome
      // is written for a nonprofit and CP-294 authorizes handing a donor's
      // existing CheckPlus account to a separate company; a business account
      // made on Plug is neither, and there is no copy pack that fits one.
      // Holding it here would show it the donor's wording under a Cancel that
      // logs it out, so it skips straight past that pane.
      //
      // The profile questions are a separate offer and it does owe those: the
      // run has its own business copy, and the stamp it spends is the
      // account's own. This is the same split the comment below describes —
      // which accounts are asked to consent does not decide who is onboarded.
      if (kind === "business") {
        this.props.vm.enterSession(kind, {
          firstRun: session.ok && session.needsOnboarding,
          // Read and passed for every account type, though only a nonprofit
          // has a tour written for it today (CP-308). enterSession is the one
          // that knows whether the workspace being opened has one, which is
          // what keeps that fact in a single place.
          firstTour: session.ok && session.needsTour,
        });
        return;
      }
      // Two ways to pass straight through. Consented already is the ordinary
      // one. The other is a nonprofit whose profile would not load: CP-295 is
      // informational, so a transient failure costs a welcome that reappears
      // next sign-in. CP-294 authorizes a transfer between two separate
      // companies, so the same failure has to become a retry — nobody should
      // reach their giving history without having seen the disclosure.
      if ((session.ok && !session.needsConsent) || (!session.ok && kind === "nonprofit")) {
        // A nonprofit that has never been offered its profile questions gets
        // them now, whether or not a consent screen stood here first (CP-307).
        // This is the fix for the regression that pairing them caused: CP-304
        // stopped asking Plug-origin accounts to consent, and because the run
        // was reached only by accepting that screen, it stopped reaching them
        // at all. The stamp is read straight from the account, so which
        // accounts are asked to consent no longer decides who is onboarded.
        //
        // session.ok is false here only for a nonprofit whose profile would not
        // load, and a first run cannot be established without having read one.
        // That costs a deferred offer, not a lost one: the stamp is still
        // absent, so the next sign-in asks again.
        this.props.vm.enterSession(kind, {
          firstRun: session.ok && session.needsOnboarding,
          firstTour: session.ok && session.needsTour,
        });
        return;
      }
      this.setState({
        submitting: false,
        authMode: "consent",
        consentKind: kind,
        consentAccount: session.account,
        consentOnboarding: session.ok && session.needsOnboarding,
        // Held beside it for the same reason: the account is read here and
        // not again, so the consent pane has to carry both first-run answers
        // through to the press that leaves it.
        consentTour: session.ok && session.needsTour,
        consentBusy: false,
        consentErr: session.ok ? "" : this.props.vm.m.consentErrAccount,
      });
    });

  toggleConsentAccount = () =>
    this.setState((s) => ({
      consentAgreeAccount: !s.consentAgreeAccount,
      consentErr: "",
    }));

  toggleConsentPayments = () =>
    this.setState((s) => ({
      consentAgreePayments: !s.consentAgreePayments,
      consentErr: "",
    }));

  // The account fetch failed and we held the donor here rather than letting
  // them past the disclosure. Retry it rather than making someone who typed
  // the right password sign in all over again.
  retryConsent = () => {
    if (this.state.consentBusy) {
      return;
    }
    this.setState({ consentBusy: true, consentErr: "" });
    this.props.vm.loadSession(this.state.consentKind).then((session) => {
      if (session.ok && !session.needsConsent) {
        this.props.vm.enterSession(this.state.consentKind, {
          firstRun: session.needsOnboarding,
          firstTour: session.needsTour,
        });
        return;
      }
      this.setState({
        consentAccount: session.account,
        consentOnboarding: session.ok && session.needsOnboarding,
        consentTour: session.ok && session.needsTour,
        consentBusy: false,
        consentErr: session.ok ? "" : this.props.vm.m.consentErrAccount,
      });
    });
  };

  /**
   * Continue / Get started. The workspace opens only once consent is recorded:
   * routing on an unconfirmed POST would show the dashboard to someone the
   * next sign-in will ask all over again, which is the one outcome both
   * screens exist to prevent.
   */
  acceptConsent = () => {
    if (this.state.consentBusy || !this.state.consentAccount) {
      return;
    }
    // A nonprofit agrees to both or does not continue. The donor pane has no
    // ticks of its own, so it is not held by them.
    if (this.state.consentKind === "nonprofit" && !this.consentAgreed()) {
      return;
    }
    this.setState({ consentBusy: true, consentErr: "" });
    this.props.vm.recordPlugConsent(this.state.consentAgreePayments).then((ok) => {
      // Same asymmetry as the profile fetch: an unrecorded welcome shows once
      // more next sign-in, which is a far smaller cost than locking a
      // nonprofit out of its own dashboard over a failed write. The donor pane
      // stays put, because routing on an unconfirmed consent is exactly the
      // outcome it exists to prevent.
      if (ok || this.state.consentKind === "nonprofit") {
        // Read from the account rather than hardcoded true: a nonprofit being
        // re-asked to consent because the disclosure version moved has already
        // been through its questions, and should not sit through them again.
        this.props.vm.enterSession(this.state.consentKind, {
          firstRun: this.state.consentOnboarding,
          firstTour: this.state.consentTour,
        });
        return;
      }
      this.setState({ consentBusy: false, consentErr: this.props.vm.m.consentErrSave });
    });
  };

  /**
   * Cancel — donors only (CP-294). Discards the session and returns to browse
   * as a guest, where guest checkout still works. They are asked again next
   * time they sign in; there is no Plug-local shadow account.
   */
  declineConsent = (logout) => () => {
    if (this.state.consentBusy) {
      return;
    }
    this.setState({ consentBusy: true });
    // login() wrote these, so refusing has to clear them too.
    localStorage.removeItem("accessToken");
    localStorage.removeItem("expiredIn");
    // logout() clears the server session and the donorAuth flag together —
    // dropping only the local flag would leave the session alive on the server
    // after an explicit refusal. Leave either way: staying on an authorization
    // screen that has already been declined helps nobody.
    const browse = () => this.props.vm.goBrowse();
    logout().then(browse, browse);
  };

  // Field sets differ per account type — a business needs its BR number, a
  // nonprofit its organisation name (Section 88 and payouts come later), an
  // individual neither.
  roles() {
    const m = this.props.vm.m;
    // Business sign-up is held back — see NOT_READY in Marketplace.js. The
    // card is filtered out rather than the entry removed, so the fields and
    // the submit branch behind it stay intact for when it returns.
    const hidden = this.props.vm.showBusinessSignup ? [] : ["business"];
    return [
      {
        id: "individual",
        label: m.loginRoleIndividual,
        // No blurb: the individual and nonprofit cards carry their label
        // alone, and what the account is for is said by the footnote under
        // the fields once the card is picked. loginBlurbIndividual is kept in
        // all three locales, translated, for whenever it is wanted back.
        footnote: m.loginFootnoteIndividual,
        cta: m.loginCtaIndividual,
        // Two name fields rather than one. The endpoint wants firstName and
        // lastName separately, and splitting a typed full name the way the
        // business card does invents a surname when someone types one word.
        fields: [
          { id: "firstName", label: m.checkoutFirstName, ph: m.loginFirstNamePh },
          { id: "lastName", label: m.checkoutLastName, ph: m.loginLastNamePh },
          { id: "email", label: m.checkoutEmail, ph: m.loginEmailPh },
          { id: "phoneNumber", label: m.checkoutPhone, ph: m.loginPhonePh },
          { id: "password", label: m.loginPassword, ph: m.loginPasswordPh, pw: true },
        ],
      },
      {
        id: "business",
        label: m.loginRoleBusiness,
        blurb: m.loginBlurbBusiness,
        footnote: m.loginFootnoteBusiness,
        cta: m.loginCtaBusiness,
        fields: [
          { id: "businessName", label: m.loginBusinessName, ph: m.loginBusinessNamePh },
          { id: "brNo", label: m.loginBrNo, ph: m.loginBrNoPh },
          { id: "email", label: m.loginWorkEmail, ph: m.loginWorkEmailPh },
          {
            id: "phone",
            kind: "phone",
            label: m.loginWorkPhone,
            ph: m.loginWorkPhonePh,
          },
          { id: "password", label: m.loginPassword, ph: m.loginPasswordPh, pw: true },
        ],
      },
      {
        id: "nonprofit",
        label: m.loginRoleNonprofit,
        // No blurb -- see the individual card above.
        footnote: m.loginFootnoteNonprofit,
        cta: m.loginCtaNonprofit,
        // Section 88 is collected later on CheckPlus. This card only claims
        // the page — contact and governance details follow on the next step.
        fields: [
          { id: "organizationName", label: m.loginOrgName, ph: m.loginOrgNamePh },
          { id: "email", label: m.loginWorkEmail, ph: m.loginWorkEmailPh },
          { id: "password", label: m.loginPassword, ph: m.loginPasswordPh, pw: true },
        ],
      },
    ].filter((role) => hidden.indexOf(role.id) < 0);
  }

  /* Sign-in and sign-up are full-page screens with no workspace chrome, so the
     header's language control is out of reach on exactly the screens where a
     visitor is most likely to want it: someone who read the marketing site in
     Chinese and clicked through lands on an English form with nothing to
     change it. Drawn on the brand panel rather than over the form, which puts
     it in the same top corner the marketing nav keeps it in.

     It reads and writes i18n through the view model (vm.language,
     vm.setLanguage) rather than AuthConsumer, unlike Header.js: this component
     renders without a provider in tests, and a bare consumer here would take
     the brand block -- and every sign-up test with it -- down. */
  renderLanguage() {
    const vm = this.props.vm;
    return (
      <div className="Marketplace-Login-lang">
        <span className="Marketplace-Login-langGlobe" />
        <select
          className="Marketplace-Login-langSelect"
          data-testid="login-language"
          aria-label={vm.m.loginLanguage}
          value={vm.language || "en"}
          onChange={(e) => vm.setLanguage && vm.setLanguage(e.target.value)}
        >
          {Object.keys(LANGUAGE_LABELS).map((code) => (
            <option key={code} value={code}>
              {LANGUAGE_LABELS[code]}
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderForgot() {
    const m = this.props.vm.m;
    return (
      <div className="Marketplace-Login-pane">
        <h3 className="Marketplace-Login-title">{m.loginForgotTitle}</h3>
        <p className="Marketplace-Login-sub">{m.loginForgotSub}</p>
        <div className="Marketplace-Login-form">
          <div>
            <div className="Marketplace-Login-label">{m.checkoutEmail}</div>
            <input
              className="Marketplace-Login-input"
              data-testid="forgot-email"
              placeholder={m.loginEmailPh}
              value={this.state.email}
              onChange={this.onField("email")}
              onKeyDown={(e) => e.key === "Enter" && this.sendReset()}
            />
          </div>
          {this.state.errMsg && <div className="Marketplace-Login-error">{this.state.errMsg}</div>}
          <div
            className={
              "Marketplace-Login-primaryBtn" +
              (this.state.submitting ? " Marketplace-Login-primaryBtn--disabled" : "")
            }
            data-testid="forgot-submit"
            onClick={this.state.submitting ? undefined : this.sendReset}
          >
            {this.state.submitting ? m.loginForgotSending : m.loginForgotCta}
          </div>
          <div className="Marketplace-Login-note">
            <span className="Marketplace-Login-link" onClick={this.setSignin}>
              {m.loginForgotBack}
            </span>
          </div>
        </div>
      </div>
    );
  }

  /* Says "if that address has an account" rather than "we have sent":
     this pane is shown whether or not one was found, and a flat claim that
     mail is on its way would be a lie in exactly the case that makes the
     answer worth hiding. */
  renderForgotSent() {
    const m = this.props.vm.m;
    return (
      <div className="Marketplace-Login-pane">
        <h3 className="Marketplace-Login-title">{m.loginForgotSentTitle}</h3>
        <p className="Marketplace-Login-sub">
          {/* Replaced through a function, not a string. A string replacement
              honours $&, $$, $` and $' as substitution patterns even when the
              thing being searched for is a plain string, and all four are
              valid atext in the local part of an address (RFC 5321). The
              worst of them is $', which repeats everything after the match --
              a$'b@example.com would print the rest of the sentence back
              mid-address. A function replacer is handed the text verbatim. */}
          {(m.loginForgotSentSub || "").replace(
            "{email}",
            () => this.state.forgotSentTo
          )}
        </p>
        <div className="Marketplace-Login-form">
          <div
            className="Marketplace-Login-primaryBtn"
            data-testid="forgot-done"
            onClick={this.setSignin}
          >
            {m.loginForgotBack}
          </div>
          <div className="Marketplace-Login-note">{m.loginForgotSentNote}</div>
        </div>
      </div>
    );
  }

  renderSignIn(marketplaceLogin) {
    const m = this.props.vm.m;
    const signIn = this.submit(marketplaceLogin);
    return (
      <div className="Marketplace-Login-pane">
        <h3 className="Marketplace-Login-title">{m.loginWelcome}</h3>
        <p className="Marketplace-Login-sub">{m.loginSignInSub}</p>
        {/* A new account lands here rather than signed in: registration sends
            a confirmation email, and nothing works until it is opened. The
            notice from the page is what an opened link leaves behind — the
            marketplace routes here once the token has been accepted. */}
        {(this.state.successMsg || this.props.vm.loginNotice) && (
          <div className="Marketplace-Login-success">
            {this.state.successMsg || this.props.vm.loginNotice}
          </div>
        )}
        <div className="Marketplace-Login-form">
          <div>
            <div className="Marketplace-Login-label">{m.checkoutEmail}</div>
            <input
              className="Marketplace-Login-input"
              placeholder={m.loginEmailPh}
              value={this.state.email}
              onChange={this.onField("email")}
            />
          </div>
          <div>
            <div className="Marketplace-Login-labelRow">
              <span className="Marketplace-Login-label">{m.loginPassword}</span>
              <span
                className="Marketplace-Login-forgot"
                data-testid="login-forgot"
                onClick={this.setForgot}
              >
                {m.loginForgot}
              </span>
            </div>
            <input
              type="password"
              className="Marketplace-Login-input"
              placeholder={m.loginPasswordPh}
              value={this.state.password}
              onChange={this.onField("password")}
              onKeyDown={(e) => e.key === "Enter" && signIn()}
            />
          </div>
          {this.state.errMsg && <div className="Marketplace-Login-error">{this.state.errMsg}</div>}
          <div
            className={
              "Marketplace-Login-primaryBtn" +
              (this.state.submitting ? " Marketplace-Login-primaryBtn--disabled" : "")
            }
            onClick={this.state.submitting ? undefined : signIn}
          >
            {this.state.submitting ? m.loginSigningIn : m.loginTabSignIn}
          </div>
          {/* Hidden together with the divider that introduces them — see
              NOT_READY in Marketplace.js. Neither provider is wired up, so
              these were two buttons that did nothing when pressed. */}
          {!!this.props.vm.showSocialSignIn && (
            <React.Fragment>
              <div className="Marketplace-Login-divider">
                <span className="Marketplace-Login-dividerLine" />
                <span className="Marketplace-Login-dividerText">{m.loginOr}</span>
                <span className="Marketplace-Login-dividerLine" />
              </div>
              <div className="Marketplace-Login-ssoRow">
                <div className="Marketplace-Login-ssoBtn">{m.loginGoogle}</div>
                <div className="Marketplace-Login-ssoBtn">{m.loginApple}</div>
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }

  renderSignUp() {
    const vm = this.props.vm;
    const m = vm.m;
    const roles = this.roles();
    const active = roles.find((r) => r.id === this.state.signupRole) || roles[0];
    return (
      <div className="Marketplace-Login-pane">
        <h3 className="Marketplace-Login-title">{m.loginRoleTitle}</h3>
        <p className="Marketplace-Login-sub">{m.loginRoleSub}</p>
        <div className="Marketplace-Login-roles">
          {roles.map((r) => (
            <div
              className={
                "Marketplace-Login-roleCard" +
                (r.id === active.id ? " Marketplace-Login-roleCard--on" : "")
              }
              onClick={this.pickRole(r.id)}
              key={r.id}
            >
              <span className="Marketplace-Login-roleDot" />
              <div>
                <div className="Marketplace-Login-roleLabel">{r.label}</div>
                {r.blurb && (
                  <div className="Marketplace-Login-roleBlurb">{r.blurb}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Keyed by role so switching account type builds a fresh set of
            inputs rather than reusing the last role's. The labels repeat
            across roles — every card has a password, two have a work email —
            so keying on those had React reuse the same nodes, and because the
            individual card's fields are not wired to state, the reused inputs
            went from controlled to uncontrolled: a typed email and password
            stayed on screen under the next role, no longer bound to anything,
            and edits made there were dropped. */}
        <div className="Marketplace-Login-fieldGrid" key={active.id}>
          {active.fields.map((f, i) => (
            <div key={active.id + ":" + i}>
              <div className="Marketplace-Login-label">{f.label}</div>
              {f.kind === "phone" ? (
                <PhoneInput
                  international
                  defaultCountry="HK"
                  className="Marketplace-Login-phoneInput"
                  placeholder={f.ph}
                  value={this.state.phone}
                  onChange={this.onPhone}
                />
              ) : (
                /* The email field is checked where it is left, so an
                   organisation that already has an account hears it there
                   rather than after filling in the rest of the form. */
                <input
                  className="Marketplace-Login-input"
                  placeholder={f.ph}
                  type={f.pw ? "password" : "text"}
                  {...(f.id
                    ? { value: this.state[f.id], onChange: this.onField(f.id) }
                    : {})}
                  {...(f.id === "email" ? { onBlur: this.checkEmail } : {})}
                />
              )}
            </div>
          ))}
        </div>
        {this.state.errMsg && (
          <div className="Marketplace-Login-error">
            {this.state.errMsg}
            {/* The one error with somewhere to go. */}
            {this.state.errTaken && (
              <span
                className="Marketplace-Login-link Marketplace-Login-errorLink"
                onClick={this.goSignInWithEmail}
              >
                {m.loginSignInInstead}
              </span>
            )}
          </div>
        )}
        <div
          className={
            "Marketplace-Login-primaryBtn Marketplace-Login-primaryBtn--wide" +
            (this.state.submitting ? " Marketplace-Login-primaryBtn--disabled" : "")
          }
          onClick={this.state.submitting ? undefined : this.submitSignup}
        >
          {this.state.submitting ? m.loginCreating : active.cta}
        </div>
        <div className="Marketplace-Login-note">
          {active.footnote} {m.loginAgreePrefix}
          <a
            className="Marketplace-Login-link"
            href={legalHref(vm.language, "Terms")}
            target="_blank"
            rel="noopener noreferrer"
          >
            {m.loginTerms}
          </a>
          {m.loginAgreeMiddle}
          <a
            className="Marketplace-Login-link"
            href={legalHref(vm.language, "PrivacyPolicy")}
            target="_blank"
            rel="noopener noreferrer"
          >
            {m.loginPrivacy}
          </a>
          {m.loginAgreeSuffix}
        </div>
      </div>
    );
  }

  /**
   * Second nonprofit step (CP-304) — contact person only. Director, secretary
   * and the donation-page subdomain are collected during CheckPlus onboarding.
   */
  renderNonprofitDetails() {
    const m = this.props.vm.m;
    return (
      <div className="Marketplace-Login-pane">
        <h3 className="Marketplace-Login-title">{m.loginNonprofitDetailsTitle}</h3>
        <p className="Marketplace-Login-sub">{m.loginNonprofitDetailsSub}</p>
        <div className="Marketplace-Login-fieldGrid">
          <div>
            <div className="Marketplace-Login-label">{m.checkoutFirstName}</div>
            <input
              className="Marketplace-Login-input"
              placeholder={m.loginFirstNamePh}
              value={this.state.firstName}
              onChange={this.onField("firstName")}
            />
          </div>
          <div>
            <div className="Marketplace-Login-label">{m.checkoutLastName}</div>
            <input
              className="Marketplace-Login-input"
              placeholder={m.loginLastNamePh}
              value={this.state.lastName}
              onChange={this.onField("lastName")}
            />
          </div>
          <div>
            <div className="Marketplace-Login-label">{m.checkoutPhone}</div>
            <input
              className="Marketplace-Login-input"
              placeholder={m.loginPhonePh}
              value={this.state.phoneNumber}
              onChange={this.onField("phoneNumber")}
            />
          </div>
        </div>
        {this.state.errMsg && (
          <div className="Marketplace-Login-error">
            {this.state.errMsg}
            {/* The one error with somewhere to go — the same way out the card
                offers, because the server can only answer this here. */}
            {this.state.errTaken && (
              <span
                className="Marketplace-Login-link Marketplace-Login-errorLink"
                onClick={this.goSignInWithEmail}
              >
                {m.loginSignInInstead}
              </span>
            )}
          </div>
        )}
        <div
          className={
            "Marketplace-Login-primaryBtn Marketplace-Login-primaryBtn--wide" +
            (this.state.submitting ? " Marketplace-Login-primaryBtn--disabled" : "")
          }
          onClick={this.state.submitting ? undefined : this.registerNonprofit}
        >
          {this.state.submitting ? m.loginCreating : m.loginCtaNonprofitCreate}
        </div>
        <div
          className="Marketplace-Login-note Marketplace-Login-link"
          onClick={this.backFromNonprofitDetails}
        >
          {m.loginNonprofitDetailsBack}
        </div>
        <div className="Marketplace-Login-note">{m.loginNonprofitDetailsFoot}</div>
      </div>
    );
  }

  /**
   * Collect the six digits emailed by registration (CP-303).
   *
   * This sits in the ordinary panel rather than replacing the card, because it
   * is a step inside sign-up rather than a decision about an account that
   * already exists — the brand panel stays and the tabs go, so there is
   * nowhere to wander off to mid-registration.
   *
   * There is no resend: /donors/verify-code is the only verification route on
   * the donor router. Someone who loses the email is stuck, which is why the
   * address is spelled out here rather than left implicit.
   */
  renderVerify(marketplaceLogin) {
    const m = this.props.vm.m;
    const confirm = this.verify(marketplaceLogin);
    return (
      <div className="Marketplace-Login-pane">
        <h3 className="Marketplace-Login-title">{m.loginVerifyTitle}</h3>
        <p className="Marketplace-Login-sub">
          {/* Function replacer, for the reason given on the reset pane: the
              address is typed by the person, and $-sequences in a string
              replacement are substitution patterns rather than text. */}
          {(m.loginVerifySub || "").replace("{email}", () =>
            this.state.email.trim()
          )}
        </p>
        <div className="Marketplace-Login-form">
          <div>
            <div className="Marketplace-Login-label">{m.loginVerifyCode}</div>
            <input
              className="Marketplace-Login-input"
              placeholder={m.loginVerifyCodePh}
              value={this.state.verifyCode}
              onChange={this.onField("verifyCode")}
              onKeyDown={(e) => e.key === "Enter" && confirm()}
              inputMode="numeric"
              maxLength={6}
            />
          </div>
          {this.state.errMsg && (
            <div className="Marketplace-Login-error">{this.state.errMsg}</div>
          )}
          <div
            className={
              "Marketplace-Login-primaryBtn" +
              (this.state.submitting ? " Marketplace-Login-primaryBtn--disabled" : "")
            }
            onClick={this.state.submitting ? undefined : confirm}
          >
            {this.state.submitting ? m.loginVerifying : m.loginVerifyCta}
          </div>
          <div className="Marketplace-Login-note">{m.loginVerifyNote}</div>
        </div>
      </div>
    );
  }

  /**
   * The first-run screen. One shell, two jobs (CP-294, CP-295) — and the three
   * things that keep them from being mistaken for each other are the heading,
   * how the list is framed ("Plug will receive" against "You can now"), and
   * whether there is anything to refuse. A donor is being asked to authorize a
   * transfer between two companies and gets Cancel; a nonprofit is being told
   * about a state that already exists and gets one button.
   */
  // Whether the nonprofit has agreed to both of the welcome's terms.
  consentAgreed = () =>
    this.state.consentAgreeAccount && this.state.consentAgreePayments;

  renderConsent(logout) {
    const m = this.props.vm.m;
    const account = this.state.consentAccount;
    // From the kind, not the account: the failure state has no account, and
    // reading the wrong copy pack for it would put a donor's heading on a
    // nonprofit's screen.
    const isNp = this.state.consentKind === "nonprofit";
    const c = (isNp ? m.consentNonprofit : m.consentDonor) || {};
    const scopes = Array.isArray(c.scopes) ? c.scopes : [];
    const busy = this.state.consentBusy;
    return (
      <div className="Marketplace-Login Marketplace-Login--consent">
        <div className="Marketplace-Consent Marketplace-animIn">
          <img src={plugMark} alt="Plug" className="Marketplace-Consent-mark" />
          <h3 className="Marketplace-Consent-title">{c.heading}</h3>
          <p className="Marketplace-Consent-sub">{c.sub}</p>

          {/* No account row when the profile could not be read — naming the
              wrong account on an authorization screen is worse than naming
              none, so that case is only ever the error and its retry. */}
          {account && (
            <div className="Marketplace-Consent-account">
              <div
                className={
                  "Marketplace-Consent-avatar" + (isNp ? " Marketplace-Consent-avatar--org" : "")
                }
              >
                {account.initials}
              </div>
              <div className="Marketplace-Consent-who">
                <div className="Marketplace-Consent-name">{account.name}</div>
                <div className="Marketplace-Consent-email">{account.email}</div>
              </div>
              {account.tagged && <span className="Marketplace-Consent-tag">{c.orgTag}</span>}
            </div>
          )}

          {account && (
            <div className="Marketplace-Consent-scopes">
              <div className="Marketplace-Consent-scopesTitle">{c.listTitle}</div>
              {scopes.map((text) => (
                <div className="Marketplace-Consent-scope" key={text}>
                  <span className="Marketplace-Consent-check">✓</span>
                  <span className="Marketplace-Consent-scopeText">{text}</span>
                </div>
              ))}
            </div>
          )}

          {/* The artboard asks this as onboarding's last question. It is
              here instead because this screen already exists to record
              consent, and asking the same thing twice in a row would be one
              agreement too many. */}
          {account && isNp && (
            <div className="Marketplace-Consent-agree">
              <div className="Marketplace-Consent-agreeTitle">{c.agreeTitle}</div>
              <div className="Marketplace-Consent-agreeBody">{c.agreeBody}</div>
              <div
                className="Marketplace-Consent-tick"
                role="checkbox"
                aria-checked={this.state.consentAgreeAccount}
                onClick={this.toggleConsentAccount}
              >
                <span
                  className={
                    "Marketplace-Consent-box" +
                    (this.state.consentAgreeAccount ? " Marketplace-Consent-box--on" : "")
                  }
                >
                  {this.state.consentAgreeAccount ? "✓" : ""}
                </span>
                <span className="Marketplace-Consent-tickText">{c.agreeAccount}</span>
              </div>
              <div
                className="Marketplace-Consent-tick"
                role="checkbox"
                aria-checked={this.state.consentAgreePayments}
                onClick={this.toggleConsentPayments}
              >
                <span
                  className={
                    "Marketplace-Consent-box" +
                    (this.state.consentAgreePayments ? " Marketplace-Consent-box--on" : "")
                  }
                >
                  {this.state.consentAgreePayments ? "✓" : ""}
                </span>
                <span className="Marketplace-Consent-tickText">{c.agreePayments}</span>
              </div>
            </div>
          )}

          {this.state.consentErr && (
            <div className="Marketplace-Login-error Marketplace-Consent-error">{this.state.consentErr}</div>
          )}

          <div className="Marketplace-Consent-actions">
            {account ? (
              <div
                className={
                  "Marketplace-Consent-primary" +
                  (busy || (isNp && !this.consentAgreed())
                    ? " Marketplace-Consent-primary--disabled"
                    : "")
                }
                onClick={busy || (isNp && !this.consentAgreed()) ? undefined : this.acceptConsent}
              >
                {busy ? m.consentWorking : c.cta}
              </div>
            ) : (
              <div
                className={
                  "Marketplace-Consent-primary" + (busy ? " Marketplace-Consent-primary--disabled" : "")
                }
                onClick={busy ? undefined : this.retryConsent}
              >
                {busy ? m.consentWorking : m.consentRetry}
              </div>
            )}
            {/* Cancel is CP-294 only, and it belongs on the failure state too:
                someone who cannot get past a broken profile fetch still needs a
                way out that does not leave them signed in. */}
            {!isNp && (
              <div
                className={
                  "Marketplace-Consent-secondary" + (busy ? " Marketplace-Consent-secondary--disabled" : "")
                }
                onClick={busy ? undefined : this.declineConsent(logout)}
              >
                {c.cancel}
              </div>
            )}
          </div>

          <div className="Marketplace-Consent-foot">{c.foot}</div>
        </div>
      </div>
    );
  }

  render() {
    const vm = this.props.vm;
    const m = vm.m;
    if (this.state.authMode === "consent") {
      return <AuthConsumer>{({ logout }) => this.renderConsent(logout)}</AuthConsumer>;
    }
    const isSignin = this.state.authMode === "signin";
    // Verification and the nonprofit details step are mid-registration, so
    // the tabs come off: switching away would abandon a half-finished account
    // or offer a form that refuses the address just typed.
    const isVerify = this.state.authMode === "verify";
    const isNonprofitDetails = this.state.authMode === "nonprofitDetails";
    // The reset panes lose the tabs for a different reason than the two
    // above: nothing is half-finished, but "Sign in" is where the person
    // just came from and could not get through, so offering it as a tab
    // beside the way out of that is noise. Both panes carry their own way
    // back.
    const isForgot = this.state.authMode === "forgot";
    const isForgotSent = this.state.authMode === "forgotSent";
    const hideTabs = isVerify || isNonprofitDetails || isForgot || isForgotSent;
    return (
      <div className="Marketplace-Login Marketplace-animIn">
        <div className="Marketplace-Login-card">
          <div className="Marketplace-Login-brand">
            <div className="Marketplace-Login-brandTop">
              {/* Dismisses the pane into the marketplace, and clears ?login= so
                  a refresh does not reopen it. Tinted white the way
                  .PlugLanding-flowHub does it, rather than sat on a white
                  chip. */}
              <div className="Marketplace-Login-brandMark" onClick={vm.goWebapp}>
                <img src={plugLogo} alt="Plug" className="Marketplace-Login-brandLogo" />
                <span className="Marketplace-Login-brandBeta">{m.beta}</span>
              </div>
              {this.renderLanguage()}
            </div>
            <h2 className="Marketplace-Login-brandTitle">{m.loginPanelTitle}</h2>
            <p className="Marketplace-Login-brandBlurb">{m.loginPanelBlurb}</p>
            <div className="Marketplace-Login-brandStats">
              <div>
                <div className="Marketplace-Login-brandStatValue">
                  {vm.orgsLoading || vm.orgsError ? "—" : vm.verifiedOrgCount}
                </div>
                <div className="Marketplace-Login-brandStatLabel">{m.heroVerifiedNonprofits}</div>
              </div>
            </div>
          </div>
          <div className="Marketplace-Login-panel">
            {!hideTabs && (
              <div className="Marketplace-Login-tabsRow">
                <div className="Marketplace-Login-tabs">
                  <div
                    className={"Marketplace-Login-tab" + (isSignin ? " Marketplace-Login-tab--on" : "")}
                    onClick={this.setSignin}
                  >
                    {m.loginTabSignIn}
                  </div>
                  <div
                    className={"Marketplace-Login-tab" + (!isSignin ? " Marketplace-Login-tab--on" : "")}
                    onClick={this.setSignup}
                  >
                    {m.loginTabCreate}
                  </div>
                </div>
              </div>
            )}
            {isVerify ? (
              <AuthConsumer>{({ marketplaceLogin }) => this.renderVerify(marketplaceLogin)}</AuthConsumer>
            ) : isNonprofitDetails ? (
              this.renderNonprofitDetails()
            ) : isForgot ? (
              this.renderForgot()
            ) : isForgotSent ? (
              this.renderForgotSent()
            ) : isSignin ? (
              <AuthConsumer>{({ marketplaceLogin }) => this.renderSignIn(marketplaceLogin)}</AuthConsumer>
            ) : (
              this.renderSignUp()
            )}
          </div>
        </div>
      </div>
    );
  }
}

Login.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default Login;
